import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, ShieldCheck, Scale, CheckCircle2, XCircle, Undo2, PackageCheck } from "lucide-react";

const g2kg = (g) => (Number(g || 0) / 1000).toLocaleString("id-ID", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const CONDS = ["SEALED_UTUH", "OPEN_PARTIAL", "QUARANTINE", "NOT_FOUND"];

export default function CatchWeightMigration() {
  const { can } = useAuth();
  const canStage = can("inventory.catch_weight.migrate.stage");
  const canApply = can("inventory.catch_weight.migrate");
  const [batches, setBatches] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadBatches = () => { setBatches(null); api.get("/inventory/catch-weight/migration/batches").then((r) => setBatches(r.data)).catch((e) => { toast.error(apiError(e)); setBatches([]); }); };
  useEffect(loadBatches, []);

  return (
    <div data-testid="cw-migration-page">
      <PageHeader title="Migrasi Catch Weight (CW1E)" subtitle="Inisialisasi fabric_rolls dari roll fisik existing. Tidak mengubah saldo ROLL, tanpa jurnal, flag tetap OFF." testId="cw-migration-header">
        {canStage && <Button onClick={() => setOpenCreate(true)} data-testid="cw-mig-new-batch-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" />Batch Migrasi Baru</Button>}
      </PageHeader>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!batches ? <Loading /> : batches.length === 0 ? <EmptyState title="Belum ada batch migrasi" icon={Boxes} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No Batch</TableHead><TableHead>Item</TableHead><TableHead>Gudang</TableHead>
              <TableHead className="text-right">Expected Roll</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.id} data-testid={`cw-mig-batch-${b.batch_no}`}>
                  <TableCell className="font-mono text-xs">{b.batch_no}</TableCell>
                  <TableCell>{b.item_name}</TableCell>
                  <TableCell className="text-slate-500">{b.warehouse_name}</TableCell>
                  <TableCell className="text-right">{b.expected_roll_count}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(b.id)} data-testid={`cw-mig-open-${b.batch_no}`}>Kelola</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {openCreate && <CreateBatchDialog onClose={() => setOpenCreate(false)} onCreated={(id) => { setOpenCreate(false); loadBatches(); setDetailId(id); }} />}
      {detailId && <BatchDetailDialog bid={detailId} canStage={canStage} canApply={canApply} onClose={() => { setDetailId(null); loadBatches(); }} onChange={loadBatches} />}
    </div>
  );
}

function CreateBatchDialog({ onClose, onCreated }) {
  const [cands, setCands] = useState(null);
  const [sel, setSel] = useState(null);
  const [cost, setCost] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/inventory/catch-weight/migration/candidates").then((r) => setCands(r.data.candidates)).catch((e) => { toast.error(apiError(e)); setCands([]); }); }, []);

  const create = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await api.post("/inventory/catch-weight/migration/batches", {
        inventory_item_id: sel.inventory_item_id, warehouse_id: sel.warehouse_id,
        legacy_purchase_cost_per_roll: cost === "" ? null : parseInt(cost, 10),
      });
      toast.success(`Batch ${r.data.batch_no} dibuat`);
      onCreated(r.data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="cw-mig-create-dialog">
        <DialogHeader><DialogTitle>Batch Migrasi Baru</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Pilih item kain non-catch-weight dengan saldo ROLL fisik untuk diinisialisasi. Quantity = saldo fisik; cost per KG tetap belum tersedia.</p>
        {!cands ? <Loading /> : cands.length === 0 ? <EmptyState title="Tidak ada kandidat migrasi" subtitle="Item harus non-catch-weight dengan saldo ROLL > 0." icon={Boxes} /> : (
          <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader><TableRow><TableHead></TableHead><TableHead>Item</TableHead><TableHead>Gudang</TableHead><TableHead className="text-right">On Hand</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {cands.map((c, i) => (
                  <TableRow key={i} className={sel === c ? "bg-indigo-50" : ""} data-testid={`cw-mig-cand-${i}`}>
                    <TableCell><input type="radio" name="cand" disabled={c.already_migrated} checked={sel === c} onChange={() => setSel(c)} data-testid={`cw-mig-cand-radio-${i}`} /></TableCell>
                    <TableCell>{c.item_name}</TableCell>
                    <TableCell className="text-slate-500">{c.warehouse_name}</TableCell>
                    <TableCell className="text-right">{c.on_hand}</TableCell>
                    <TableCell>{c.already_migrated ? <Badge variant="outline" className="bg-slate-100 text-slate-500">Sudah migrasi</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Kandidat</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="grid gap-1.5">
          <Label>Legacy Purchase Cost / Roll (referensi, opsional)</Label>
          <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="cth: 3200000" data-testid="cw-mig-legacy-cost" />
          <span className="text-xs text-slate-400">Hanya metadata referensi — TIDAK dipakai sebagai cost per KG.</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={create} disabled={!sel || busy} data-testid="cw-mig-create-submit" className="bg-indigo-600 hover:bg-indigo-500">Buat Batch</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchDetailDialog({ bid, canStage, canApply, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ proposed_roll_no: "", actual_weight_g: "", physical_condition: "SEALED_UTUH" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/inventory/catch-weight/migration/batches/${bid}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [bid]);

  const editable = data && ["DRAFT", "VALIDATED"].includes(data.status);

  const addRow = async () => {
    if (!form.proposed_roll_no || !form.actual_weight_g) { toast.error("Roll no & berat wajib"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/catch-weight/migration/batches/${bid}/staging`, { rows: [{ proposed_roll_no: form.proposed_roll_no, actual_weight_g: parseInt(form.actual_weight_g, 10), physical_condition: form.physical_condition }] });
      setForm({ proposed_roll_no: "", actual_weight_g: "", physical_condition: form.physical_condition });
      await load(); onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const delRow = async (sid) => { try { await api.delete(`/inventory/catch-weight/migration/staging/${sid}`); await load(); onChange(); } catch (e) { toast.error(apiError(e)); } };

  const act = async (verb, okMsg) => {
    setBusy(true); setErrors([]); setReport(null);
    try {
      const r = await api.post(`/inventory/catch-weight/migration/batches/${bid}/${verb}`);
      if (verb === "validate") { setErrors(r.data.errors || []); if (r.data.ok) toast.success("Validasi lolos"); else toast.error("Validasi gagal"); }
      else if (verb === "verify") { setReport(r.data.report); if (r.data.ok) toast.success("Invariant VERIFIED"); else toast.error("Invariant belum lolos"); }
      else toast.success(okMsg);
      await load(); onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Memuat batch…</DialogTitle></DialogHeader><Loading /></DialogContent></Dialog>;
  const totalG = (data.staging || []).reduce((s, r) => s + (r.actual_weight_g || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cw-mig-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-3">{data.batch_no} · {data.item_name} <StatusBadge status={data.status} /></DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
          <Info label="Gudang" value={data.warehouse_name} />
          <Info label="Expected Roll (saldo)" value={data.expected_roll_count} />
          <Info label="Saldo ROLL live" value={data.live_on_hand} />
          <Info label="Staged / Valid" value={`${data.staged_count} / ${data.validated_count}`} />
          <Info label="Cost / KG" value={<span className="italic text-slate-400">Belum tersedia (UNAVAILABLE)</span>} />
          <Info label="Legacy cost/Roll (ref)" value={data.legacy_purchase_cost_per_roll ? `Rp ${Number(data.legacy_purchase_cost_per_roll).toLocaleString("id-ID")}` : "-"} />
          <Info label="Total berat staging" value={`${g2kg(totalG)} KG`} />
          <Info label="Count Gate" value={data.count_gate_ok ? <Badge className="bg-emerald-100 text-emerald-700">OK</Badge> : <Badge className="bg-rose-100 text-rose-700">Belum</Badge>} />
        </div>

        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Roll No</TableHead><TableHead className="text-right">Berat (g)</TableHead><TableHead className="text-right">KG</TableHead><TableHead>Kondisi</TableHead><TableHead>Row</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {(data.staging || []).length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">Belum ada roll staging</TableCell></TableRow> :
                data.staging.map((r) => (
                  <TableRow key={r.id} data-testid={`cw-mig-row-${r.proposed_roll_no}`}>
                    <TableCell className="font-mono text-xs">{r.proposed_roll_no}</TableCell>
                    <TableCell className="text-right">{r.actual_weight_g?.toLocaleString("id-ID")}</TableCell>
                    <TableCell className="text-right">{g2kg(r.actual_weight_g)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{r.physical_condition}</Badge></TableCell>
                    <TableCell><StatusBadge status={r.migration_status} /></TableCell>
                    <TableCell className="text-right">{editable && canStage && <Button variant="ghost" size="sm" onClick={() => delRow(r.id)} data-testid={`cw-mig-del-${r.proposed_roll_no}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {editable && canStage && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end rounded-lg bg-slate-50 p-3">
            <div className="grid gap-1"><Label className="text-xs">Roll No</Label><Input value={form.proposed_roll_no} onChange={(e) => setForm({ ...form, proposed_roll_no: e.target.value })} placeholder="FAB-30S-BLK-000001" data-testid="cw-mig-add-rollno" /></div>
            <div className="grid gap-1"><Label className="text-xs">Berat (gram)</Label><Input type="number" value={form.actual_weight_g} onChange={(e) => setForm({ ...form, actual_weight_g: e.target.value })} placeholder="26400" data-testid="cw-mig-add-weight" /></div>
            <div className="grid gap-1"><Label className="text-xs">Kondisi Fisik</Label>
              <Select value={form.physical_condition} onValueChange={(v) => setForm({ ...form, physical_condition: v })}>
                <SelectTrigger data-testid="cw-mig-add-cond"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={addRow} disabled={busy} data-testid="cw-mig-add-row-btn"><Plus className="mr-1 h-4 w-4" />Tambah Roll</Button>
          </div>
        )}

        {errors.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="cw-mig-errors">
            <div className="font-semibold mb-1">Validasi gagal:</div>
            <ul className="list-disc pl-5 space-y-0.5">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}
        {report && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="cw-mig-report">
            <div className="font-semibold mb-1">Laporan Invariant</div>
            <div className="grid grid-cols-2 gap-1">{Object.entries(report.checks).map(([k, v]) => <div key={k} className="flex items-center gap-1.5">{v ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}<span className="text-xs">{k}</span></div>)}</div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {canStage && ["DRAFT", "VALIDATED"].includes(data.status) && <Button variant="outline" onClick={() => act("validate")} disabled={busy} data-testid="cw-mig-validate-btn"><Scale className="mr-1 h-4 w-4" />Validasi</Button>}
          {canStage && !["APPLIED", "VERIFIED", "APPLYING"].includes(data.status) && <Button variant="outline" className="text-rose-600" onClick={() => act("reject", "Batch di-reject")} disabled={busy} data-testid="cw-mig-reject-btn">Reject</Button>}
          {canApply && data.status === "VALIDATED" && <Button onClick={() => act("approve", "Batch disetujui")} disabled={busy} data-testid="cw-mig-approve-btn" className="bg-amber-600 hover:bg-amber-500"><ShieldCheck className="mr-1 h-4 w-4" />Approve (Owner)</Button>}
          {canApply && data.status === "APPROVED" && <Button onClick={() => act("apply", "Migrasi di-apply")} disabled={busy} data-testid="cw-mig-apply-btn" className="bg-indigo-600 hover:bg-indigo-500"><PackageCheck className="mr-1 h-4 w-4" />Apply</Button>}
          {["APPLIED", "VERIFIED"].includes(data.status) && <Button variant="outline" onClick={() => act("verify")} disabled={busy} data-testid="cw-mig-verify-btn"><CheckCircle2 className="mr-1 h-4 w-4" />Verify Invariant</Button>}
          {canApply && ["APPLIED", "VERIFIED", "FAILED"].includes(data.status) && <Button variant="outline" className="text-rose-600" onClick={() => act("rollback", "Rollback selesai")} disabled={busy} data-testid="cw-mig-rollback-btn"><Undo2 className="mr-1 h-4 w-4" />Rollback</Button>}
          <Button variant="ghost" onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div className="rounded-lg border border-slate-200 bg-white px-3 py-2"><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm font-medium text-slate-700">{value}</div></div>;
}
